import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ShieldAlert, BookOpen, Heart, Shield, Phone, ArrowRight, Lock } from "lucide-react";
import { SEO, schemas } from "@/components/SEO";
import Footer from "@/components/Footer";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/home");
    }
  }, [user, navigate]);

  return (
    <>
      <SEO
        title="Ressources et soutien face aux addictions"
        description="Libr'Addict — Ressources vérifiées, accès d'urgence et soutien pour comprendre et agir face aux addictions. Anonyme, gratuit, sans jugement."
        type="website"
        canonical="/"
        schema={schemas.organization}
      />
      <div className="min-h-dvh bg-background">
        {/* Header */}
        <header className="pt-safe pb-2 px-4 sticky top-0 z-50 bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60 border-b">
          <div className="flex items-center justify-between gap-2">
            <div className="cursor-pointer" onClick={() => navigate("/")}>
              <Logo />
            </div>
            <div className="flex items-center gap-1.5">
              <ThemeToggle />
              <Button
                onClick={() => navigate("/auth")}
                className="h-11 px-3 text-xs md:h-10 md:px-3 md:text-sm"
              >
                Connexion
              </Button>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="relative px-4 pt-12 pb-16 overflow-hidden md:py-24">
          <div className="absolute inset-0 mesh-gradient opacity-40" aria-hidden="true" />
          <div className="mx-auto max-w-3xl relative z-10 text-center space-y-6">
            <h1
              className="font-bold tracking-tight leading-tight"
              style={{ fontSize: "clamp(1.75rem, 6vw, 3.5rem)" }}
            >
              Ton espace safe pour comprendre et agir face aux addictions
            </h1>
            <p
              className="text-muted-foreground max-w-2xl mx-auto"
              style={{ fontSize: "clamp(0.875rem, 3.8vw, 1.15rem)" }}
            >
              Des ressources vérifiées, un accès d'urgence, et bientôt un accompagnement professionnel. Sans jugement, gratuit, confidentiel.
            </p>
            <div className="flex flex-col gap-3 items-stretch sm:flex-row sm:justify-center sm:items-center md:gap-4">
              <Button
                size="lg"
                onClick={() => navigate("/resources")}
                className="h-12 px-6 gap-2 text-base"
              >
                <BookOpen className="w-5 h-5" />
                Découvrir les ressources
              </Button>
              <Button
                size="lg"
                variant="destructive"
                onClick={() => navigate("/panic")}
                className="h-12 px-6 gap-2 text-base"
              >
                <Phone className="w-5 h-5" />
                Numéros d'urgence
              </Button>
            </div>
          </div>
        </section>

        {/* 3 Zones */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {/* Card 1 — Urgence */}
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 space-y-4 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-destructive/15 flex items-center justify-center">
                  <ShieldAlert className="w-6 h-6 text-destructive" />
                </div>
                <h3 className="text-xl font-bold">Urgence</h3>
                <p className="text-sm text-muted-foreground">
                  Numéros d'urgence, que faire en cas de crise, premiers réflexes
                </p>
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => navigate("/panic")}
                >
                  Accéder
                </Button>
              </div>

              {/* Card 2 — S'informer */}
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-6 space-y-4 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold">S'informer</h3>
                <p className="text-sm text-muted-foreground">
                  Fiches substances, réduction des risques, timeline de sevrage, annuaire CSAPA/CJC
                </p>
                <Button
                  className="w-full"
                  onClick={() => navigate("/resources")}
                >
                  Explorer
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => navigate("/withdrawal")}>
                    Sevrage
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => navigate("/directory")}>
                    Annuaire
                  </Button>
                </div>
              </div>

              {/* Card 3 — Être accompagné */}
              <div className="relative rounded-xl border border-accent/30 bg-accent/5 p-6 space-y-4 opacity-80">
                <Badge className="absolute top-3 right-3 bg-accent/15 text-accent border-accent/30">
                  <Lock className="w-3 h-3 mr-1" />
                  Bientôt
                </Badge>
                <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-xl font-bold">Être accompagné</h3>
                <p className="text-sm text-muted-foreground">
                  Consultation avec un·e spécialiste, téléconsultation, suivi personnalisé
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/search")}
                >
                  En savoir plus
                </Button>
                <p className="text-xs italic text-muted-foreground/70 text-center">
                  Recherche de financement en cours
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pourquoi Libr'Addict */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Pourquoi Libr'Addict ?
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
              <FeatureCard
                icon={<Shield className="w-8 h-8 text-primary" />}
                title="Anonyme & Confidentiel"
                description="Tes données sont protégées. Aucun jugement."
              />
              <FeatureCard
                icon={<BookOpen className="w-8 h-8 text-accent" />}
                title="Contenu vérifié"
                description="Toutes nos ressources sont validées par des professionnels de santé."
              />
              <FeatureCard
                icon={<Heart className="w-8 h-8 text-primary" />}
                title="Sans jugement"
                description="Un espace bienveillant, quels que soient tes choix."
              />
              <FeatureCard
                icon={<Phone className="w-8 h-8 text-accent" />}
                title="Aide d'urgence"
                description="Accès rapide aux numéros et ressources de crise."
              />
            </div>
          </div>
        </section>

        {/* CTA finale */}
        <section className="py-20 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center space-y-6">
              <h2 className="text-3xl font-bold">Prêt·e à t'informer ?</h2>
              <p className="text-muted-foreground">
                Accède gratuitement à toutes nos ressources sur la réduction des risques.
              </p>
              <Button
                size="lg"
                onClick={() => navigate("/resources")}
                className="gap-2"
              >
                Commencer
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="text-center space-y-4">
      <div className="flex justify-center">{icon}</div>
      <h3 className="font-semibold text-lg">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}
